import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import {
	getCoordinationList,
	unassignCoordination
} from '../../../../../../api/nora/agencies';

import { useAuthState } from '../../../../../../contexts/AuthContext';
import {
	useAgencyState,
	useAgencyAction
} from '../../../../../../contexts/AgenciesContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import Alert from '../../../../../Alert';
import CoordinationModal from './CoordinationModal';
import FirstAndLastName from '../../../../../Table/Cells/FirstAndLastName';
import HelpModal from '../../../../../Shared/HelpModal';
import SectionHeader from '../../../../../Shared/SectionHeader';
import Table from '../../../../../Table';
import TableRowActions from '../../../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../../../Table/filterFunctions/multiWordFilterFn';

const CoordinationTab = ( { activeIndex } ) => {
	const history = useHistory();

	const { user: { permissions } } = useAuthState();
	const { agency } = useAgencyState();
	const {
		setCoordination,
		setCoordinationOptions
	} = useAgencyAction();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ coordinationModal, setCoordinationModal ] = useState( false );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ coordinatorToDelete, setCoordinatorToDelete ] = useState( {} );

	useEffect( () => {
		if ( agency?.id ) {
			setCoordinationOptions( agency?.id );
		}
	}, [
		agency,
		setCoordinationOptions
	] );

	const retrieveCoordinationList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Coordination List...' );

		try {
			const data = await getCoordinationList( agency?.id );

			setCoordination( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		agency,
		setCoordination,
		setApiErrorAlert
	] );

	const addCoordinator = useCallback( () => {
		setCoordinationModal( true );
	}, [] );

	const coordinationActions = useMemo( () => [
		{
			action: addCoordinator,
			icon: 'add',
			permission: true,
			text: 'Add Coordinator'
		}
	], [ addCoordinator ] );

	const closeCoordinationModal = useCallback( () => {
		setCoordinationModal( false );
	}, [] );

	const firstAndLastNameCell = useCallback( ( { row } ) => <FirstAndLastName row={ row.original } />, [] );

	const onView = useCallback( ( { id } ) => {
		history.push( {
			pathname: `/administration/users/${ id }`,
			state: {
				text: '\u2190\u00A0\u00A0Back to Agency',
				to: {
					pathname: `/agencies/directory/${ agency?.id }`,
					state: { activeIndex }
				}
			}
		} );
	}, [
		history,
		agency,
		activeIndex
	] );

	const onDelete = useCallback( participant => {
		setCoordinatorToDelete( participant );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewUsers.canExecute,
				icon: 'search',
				permission: permissions.viewUsers.isAvailable,
				text: 'View User'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.editAgencies.canExecute,
				icon: 'times',
				permission: permissions.editAgencies.isAvailable,
				text: 'Remove User'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDelete,
		onView,
		permissions
	] );

	const columns = useMemo( () => [
		{
			id: 'name',
			accessor: row => `${ row.firstName }${ row.lastName }`,
			Header: 'Name',
			filter: multiWordFilterFn,
			Cell: firstAndLastNameCell,
			minWidth: 60
		}
	], [ firstAndLastNameCell ] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewUsers.isAvailable || permissions.editAgencies.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const handleUnassignCoordinator = useCallback( async () => {
		setInfobarLoader( 'Retrieving Coordination List...' );

		try {
			await unassignCoordination( agency?.id, coordinatorToDelete.id );
			await retrieveCoordinationList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		agency,
		coordinatorToDelete.id,
		retrieveCoordinationList,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to remove <strong>{ `${ coordinatorToDelete.firstName } ${ coordinatorToDelete.lastName }` }</strong> from the list?</span>, [ coordinatorToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	return (
		<>
			<SectionHeader
				actions={ coordinationActions }
				title={
					<>
						<span>Agency Coordination</span>
						<HelpModal
							id={ 29 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			{ agency?.coordinators?.length
				? <Table
					columns={ renderColumns }
					data={ agency.coordinators }
					showCount={ agency.coordinators.length > 10 }
					showEmptyRows={ false }
					showPagination={ agency.coordinators.length > 10 }
				/>
				: null
			}
			<CoordinationModal
				closeModal={ closeCoordinationModal }
				open={ coordinationModal }
				retrieveCoordinationList={ retrieveCoordinationList }
			/>
			<Alert
				action={ handleUnassignCoordinator }
				name="unassign-coordination-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</>
	);
};

CoordinationTab.propTypes = { activeIndex: PropTypes.number };

export default CoordinationTab;
